import { useEffect, useMemo } from "react";
import { Link, useParams, Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Users, Compass, Sparkles, Church, Flower2, Mountain, Camera,
  Briefcase, Tent, CalendarCheck, Car, Ship, Map, Plane, Crown, Palmtree,
  Leaf, Landmark, ArrowRight, ChevronRight,
} from "lucide-react";
import { Header } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { FloatingWidgets } from "@/components/sections/FloatingWhatsApp";
import { SmartImage } from "@/components/SmartImage";
import { TourCard } from "@/components/india/TourCard";
import { CallbackCard } from "@/components/india/EnquiryForm";
import { Awards, TourReviews } from "@/components/india/IndiaSections";
import { getCategory, getCategoryTours, getItemTours } from "@/data/categories";
import { matchText } from "@/lib/search";
import { TOUR_REVIEWS } from "@/data/indiaContent";

const ICONS = {
  Heart, Users, Compass, Sparkles, Church, Flower2, Mountain, Camera,
  Briefcase, Tent, CalendarCheck, Car, Ship, Map, Plane, Crown, Palmtree,
  Leaf, Landmark,
};

export default function CategoryPage() {
  const { key } = useParams();
  const cfg = getCategory(key);
  const [params, setParams] = useSearchParams();
  const item = params.get("item") || "";
  const tours = useMemo(() => {
    if (!cfg) return [];
    return item ? getItemTours(cfg, item, matchText) : getCategoryTours(cfg);
  }, [cfg, item]);

  useEffect(() => {
    if (!item) return;
    const id = cfg?.showTours ? "category-tours-anchor" : "category-enquiry";
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  }, [item, cfg]);

  if (!cfg) return <Navigate to="/" replace />;

  return (
    <div className="bg-white text-[var(--navy)] overflow-x-hidden">
      <Header />

      {/* Hero */}
      <section className="relative h-[380px] md:h-[440px] overflow-hidden" data-testid="category-hero">
        <SmartImage src={cfg.hero} alt={cfg.label} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b2545]/90 via-[#0b2545]/70 to-[#0b2545]/30" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-[1280px] mx-auto px-4 lg:px-6 w-full">
            <nav className="text-[12px] text-white/70 mb-4">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-1.5">&gt;</span>
              <span className="text-white font-medium">{cfg.label}</span>
            </nav>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-serif-display text-4xl md:text-6xl text-white max-w-2xl leading-[1.05]"
            >
              {cfg.label}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="text-white/85 text-lg mt-4 max-w-xl"
            >
              {cfg.tagline}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Intro + category tiles */}
      <section className="py-14 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
          <p className="max-w-3xl text-[15px] text-gray-600 leading-relaxed mb-10">{cfg.description}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-testid="category-tiles">
            {cfg.items.map((tile, i) => {
              const Icon = ICONS[tile.icon] || Sparkles;
              const active = tile.label === item;
              return (
                <motion.button
                  type="button"
                  key={tile.label}
                  onClick={() => setParams(active ? {} : { item: tile.label })}
                  data-testid={`category-tile-${tile.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                  className={`group text-left bg-white border rounded-xl p-5 ${active ? "border-[var(--yellow-2)] ring-2 ring-[#fcd34d]/40" : "border-gray-200"} hover:border-[#f59e0b]/50 hover:shadow-[0_18px_40px_-24px_rgba(11,37,69,0.4)] transition-all`}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#fcd34d]/15 flex items-center justify-center mb-3 group-hover:bg-[var(--yellow)] transition-colors">
                    <Icon className="w-6 h-6 text-[var(--yellow-2)] group-hover:text-[var(--navy)]" strokeWidth={1.6} />
                  </div>
                  <div className="font-semibold text-[15px] text-[var(--navy)]">{tile.label}</div>
                  <div className="text-[13px] text-gray-500 mt-0.5">{tile.desc}</div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tours grid (when applicable) */}
      <div id="category-tours-anchor" className="scroll-mt-28" />
      {cfg.showTours && (tours.length > 0 || item) && (
        <section className="py-14 bg-[var(--soft)]" data-testid="category-tours">
          <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
            <div className="text-center mb-9">
              <h2 className="font-serif-display text-2xl md:text-[32px] text-[var(--navy)] brush-underline" data-testid="category-tours-title">{item ? `${item} Tours` : `Featured ${cfg.label}`}</h2>
              {item && (
                <div className="mt-4">
                  <button type="button" onClick={() => setParams({})} data-testid="category-clear-item" className="text-[13px] font-semibold underline text-[var(--navy)]">
                    Show all {cfg.label}
                  </button>
                </div>
              )}
            </div>
            {tours.length === 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-500" data-testid="category-no-tours">
                No ready-made {item} packages right now — share your details below and our experts will design one for you.
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {tours.map((t, i) => <TourCard key={t.slug} tour={t} view="grid" index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* Enquiry band (esp. for service categories) */}
      <section className="py-16 bg-white scroll-mt-28" id="category-enquiry" data-testid="category-enquiry">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 grid lg:grid-cols-[1fr_360px] gap-10 items-center">
          <div>
            <h2 className="font-serif-display text-3xl md:text-[40px] text-[var(--navy)] leading-tight">
              Let's plan your <span className="text-[var(--yellow-2)]">{(item || cfg.label).split(" ").map((w) => (w === w.toUpperCase() ? w : w.toLowerCase())).join(" ")}</span>
            </h2>
            <p className="text-[15px] text-gray-600 mt-4 max-w-xl leading-relaxed">
              Share a few details and our travel experts will craft a personalised proposal for you — with the best prices, handpicked stays and complete peace of mind.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="tel:+917052163551" className="inline-flex items-center gap-2 bg-[var(--navy)] hover:bg-[var(--navy-2)] text-white font-semibold px-6 py-3 rounded-md transition" data-testid="category-call">
                Call +91-7052163551
              </a>
              <Link to="/india" className="inline-flex items-center gap-2 border border-[var(--navy)] text-[var(--navy)] hover:bg-[var(--navy)] hover:text-white font-semibold px-6 py-3 rounded-md transition" data-testid="category-explore-india">
                Explore India Tours <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <CallbackCard />
        </div>
      </section>

      <Awards />
      <TourReviews config={{ data: TOUR_REVIEWS }} />
      <Footer />
      <FloatingWidgets />
    </div>
  );
}
